import React, { useState, useRef, useEffect } from 'react';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import gmail from '../../../images/misc/gmail-icon.svg';
import { FormProvider, useFormContext } from './FormContext';
import {
  getRecipientEmails,
  supportsDynamicTargeting,
  RECIPIENT_TYPES,
} from './recipientTargeting';

const FormContent = ({
  title,
  children,
  recipient,
  recipientEmail,
  ccEmail = '',
  subject = 'Support for this important initiative',
  enableDynamicRecipients = false,
  recipientType = 'static',
}) => {
  const {
    campaignId,
    campaignContext,
    systemPrompt,
    formData,
    hasValue,
    buildUserContext,
    getFieldValue,
  } = useFormContext();

  const [generatedEmail, setGeneratedEmail] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Dynamic recipient state (only used when enableDynamicRecipients is true)
  const [currentRecipientEmail, setCurrentRecipientEmail] = useState(recipientEmail);
  const [currentCcEmail, setCurrentCcEmail] = useState(ccEmail);
  const [currentRecipient, setCurrentRecipient] = useState(recipient);

  const emailSectionRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMobile = useCheckMobileScreen();

  // Build hidden CC for sends (not shown in UI)
  const hiddenCc = campaignId ? `act+${campaignId}@growsf.org` : '';
  const combineEmails = (a, b) => {
    const parts = [a, b]
      .filter(Boolean)
      .flatMap((s) => String(s).split(','))
      .map((s) => s.trim())
      .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const email of parts) {
      const key = email.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(email);
      }
    }
    return out.join(',');
  };

  // Recipient resolution (uses targeting helper for all types)
  useEffect(() => {
    const district = getFieldValue('district');

    const targetingResult = getRecipientEmails(recipientType, district, recipientEmail, ccEmail);

    setCurrentRecipientEmail(targetingResult.recipientEmail);
    setCurrentCcEmail(targetingResult.ccEmail);
    setCurrentRecipient(targetingResult.recipient);
  }, [recipientType, getFieldValue('district'), recipientEmail, ccEmail, recipient]);

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
    }
  };

  const generateEmailStreaming = async () => {
    setIsGenerating(true);
    setError('');
    setGeneratedEmail('');
    setGeneratedSubject('');

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const requestData = {
      campaignId,
      campaignContext,
      systemPrompt,
      recipient: currentRecipient,
      userContext: buildUserContext(),
      ...formData,
    };

    const startTime = Date.now();

    try {
      const response = await fetch('/api/generate-email-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        let errorData = {};
        try {
          const errorText = await response.text();
          errorData = errorText ? JSON.parse(errorText) : {};
        } catch (parseError) {
          errorData = {};
        }
        if (response.status === 429) {
          setError('Rate limit exceeded. Please try again in a few moments.');
        } else if (response.status === 400) {
          if (errorData.type === 'empty_response') {
            setError('Email generation failed - please try simplifying your request or try again.');
          } else if (errorData.type === 'length_limit_exceeded') {
            setError(
              'Email was cut off due to length. Please shorten your custom notes and try again.',
            );
          } else {
            setError(errorData.error || 'Invalid request. Please check your inputs.');
          }
        } else {
          setError(errorData.error || 'Failed to generate email');
        }
        return;
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.subject) {
                  setGeneratedSubject(data.subject);
                }

                if (data.content) {
                  accumulatedContent += data.content;
                  setGeneratedEmail(accumulatedContent);

                  // Scroll to email section on first content if not visible
                  if (accumulatedContent.length < 50 && emailSectionRef.current) {
                    const rect = emailSectionRef.current.getBoundingClientRect();
                    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

                    if (!isVisible) {
                      emailSectionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    }
                  }
                }

                if (data.done) {
                  const totalTime = Date.now() - startTime;
                  console.log(`Total streaming time: ${totalTime}ms`);
                  return;
                }

                if (data.error) {
                  setError(data.error);
                  return;
                }
              } catch (e) {
                // Skip malformed JSON lines
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Generation was cancelled');
        setError('Generation cancelled');
      } else {
        console.error('Error generating email:', error);
        setError('Failed to generate email. Please try again.');
      }
    } finally {
      abortControllerRef.current = null;
      setIsGenerating(false);
    }
  };

  const generateEmail = () => {
    if (!hasValue('name')) return;
    generateEmailStreaming();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isGenerating && hasValue('name')) {
      e.preventDefault();
      generateEmail();
    }
  };

  const sendEmail = () => {
    const finalSubject = generatedSubject || subject;
    const encodedSubject = encodeURIComponent(finalSubject);
    const encodedBody = encodeURIComponent(generatedEmail);
    const fullCc = combineEmails(currentCcEmail, hiddenCc);
    const encodedCC = fullCc ? encodeURIComponent(fullCc) : '';

    let mailtoLink = `mailto:${currentRecipientEmail}?subject=${encodedSubject}&body=${encodedBody}`;
    if (encodedCC) {
      mailtoLink += `&cc=${encodedCC}`;
    }
    window.open(mailtoLink, '_blank');
  };

  const sendViaGmail = () => {
    const finalSubject = generatedSubject || subject;
    const gmailParams = new URLSearchParams();
    gmailParams.append('view', 'cm');
    gmailParams.append('fs', '1');
    gmailParams.append('to', currentRecipientEmail);
    const fullCc = combineEmails(currentCcEmail, hiddenCc);
    if (fullCc) gmailParams.append('cc', fullCc);
    gmailParams.append('su', finalSubject);
    gmailParams.append('body', generatedEmail);

    const gmailLink = `https://mail.google.com/mail/?${gmailParams.toString()}`;
    window.open(gmailLink, '_blank');
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg shadow-lg">
      <h3 className="text-xl font-bold mb-4">{title}</h3>

      <div className="space-y-4" onKeyPress={handleKeyPress}>
        {/* Render child components */}
        {children}

        {/* Generate/Stop Button */}
        <div className="flex gap-2">
          <button
            onClick={isGenerating ? stopGeneration : generateEmail}
            disabled={!isGenerating && !hasValue('name')}
            className={`w-auto px-4 py-2 font-semibold text-white rounded-md text-sm flex items-center justify-center ${
              !isGenerating && !hasValue('name')
                ? 'bg-gray-400 cursor-not-allowed'
                : isGenerating
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-brand-red-3 hover:bg-red-600'
            }`}
          >
            {isGenerating && (
              <svg
                className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="m4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isGenerating ? 'Stop' : 'Generate Email'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="!my-0 text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Generated Email */}
        {generatedEmail && (
          <div ref={emailSectionRef} className="mt-4 space-y-3">
            <h4 className="text-base font-semibold">Your Generated Email</h4>

            {generatedSubject && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Subject:</label>
                <input
                  type="text"
                  value={generatedSubject}
                  onChange={(e) => setGeneratedSubject(e.target.value)}
                  className="w-full p-2 border rounded-md text-sm bg-white"
                />
              </div>
            )}

            <textarea
              value={generatedEmail}
              onChange={(e) => setGeneratedEmail(e.target.value)}
              className="w-full p-3 border rounded-md text-sm bg-white"
              rows={10}
            />

            <div className="flex gap-2 flex-col sm:flex-row">
              <button
                onClick={sendEmail}
                disabled={isGenerating}
                className={`px-4 py-2 font-semibold rounded-md text-sm w-full ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-brand-red-3 hover:bg-red-600 text-white'
                }`}
              >
                Send Email
              </button>
              {!isMobile && (
                <button
                  onClick={sendViaGmail}
                  disabled={isGenerating}
                  className={`flex items-center justify-center px-3 py-2 border font-medium rounded-md text-sm transition duration-200 whitespace-nowrap w-full ${
                    isGenerating
                      ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-brand-red-3 text-gray-700'
                  }`}
                >
                  <img src={gmail} className="w-4 h-4 !my-0 mr-1" alt="Gmail" />
                  Gmail
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recipients info */}
        <p className="!text-[10px] !text-gray-500 !leading-relaxed">
          <span className="font-medium">To:</span> {currentRecipientEmail?.replace(/,/g, ', ')}
          {currentCcEmail && (
            <>
              <br />
              <span className="font-medium">CC:</span> {currentCcEmail?.replace(/,/g, ', ')}
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export const AIEmailWriterForm = ({
  campaignId = 'family-zoning',
  campaignContext,
  systemPrompt,
  recipient,
  recipientEmail,
  ccEmail = '',
  subject = 'Support for this important initiative',
  enableDynamicRecipients = false,
  recipientType = 'static', // 'static', 'supervisors', 'planning-commission', etc.
  children,
  title = 'Generate Your Personalized Email',
}) => {
  return (
    <FormProvider
      campaignId={campaignId}
      campaignContext={campaignContext}
      systemPrompt={systemPrompt}
    >
      <FormContent
        title={title}
        recipient={recipient}
        recipientEmail={recipientEmail}
        ccEmail={ccEmail}
        subject={subject}
        enableDynamicRecipients={enableDynamicRecipients}
        recipientType={recipientType}
      >
        {children}
      </FormContent>
    </FormProvider>
  );
};
